import type {
  SemanticClassWire,
  StreamCandidate,
  StreamTransaction,
  StreamWhy,
} from '@/types/onboarding';

// ---------------------------------------------------------------------------
// Stream card copy — the API states the facts (suggestedClass + why), the UI
// owns the pt-BR voice. One entry per SemanticClassWire value so an unexpected
// suggestion still renders a coherent card instead of a blank one.
// ---------------------------------------------------------------------------

export interface StreamOption {
  class: SemanticClassWire;
  label: string;
}

interface StreamCopy {
  question: string;
  options: StreamOption[];
}

const STREAM_COPY: Record<SemanticClassWire, StreamCopy> = {
  income: {
    question: 'Esse dinheiro que entra todo mês é renda sua?',
    options: [
      { class: 'income', label: 'É renda' },
      { class: 'neutral', label: 'É dinheiro meu mudando de conta' },
    ],
  },
  neutral: {
    question: 'Aqui o dinheiro só muda de lugar. Confere?',
    options: [
      { class: 'neutral', label: 'Confere' },
      { class: 'income', label: 'Não, isso é dinheiro que entra' },
    ],
  },
  cardPayment: {
    question: 'Isso é o pagamento da sua fatura?',
    options: [
      { class: 'cardPayment', label: 'É pagamento de fatura' },
      { class: 'spend', label: 'É uma compra' },
    ],
  },
  investment: {
    question: 'Isso é dinheiro que você guardou?',
    options: [
      { class: 'investment', label: 'Guardei ou investi' },
      { class: 'spend', label: 'É uma compra' },
    ],
  },
  spend: {
    question: 'Isso é um gasto seu?',
    options: [
      { class: 'spend', label: 'É um gasto' },
      { class: 'neutral', label: 'É dinheiro meu mudando de conta' },
    ],
  },
};

export function streamCopy(suggestedClass: SemanticClassWire): StreamCopy {
  return STREAM_COPY[suggestedClass] ?? STREAM_COPY.income;
}

export const CLASS_LABEL: Record<SemanticClassWire, string> = {
  income: 'Renda',
  neutral: 'Transferência entre contas suas',
  cardPayment: 'Pagamento de fatura',
  investment: 'Investimento',
  spend: 'Gasto',
};

export const DEFER_LABEL = 'Deixado para depois';

/** The "por que perguntamos?" copy, composed from the API's structured facts. */
export function whyCopy(why: StreamWhy): string {
  const parts: string[] = [];
  if (why.payerIsSelf) parts.push('O pagador tem o seu CPF — o dinheiro sai de uma conta com o seu nome.');
  if (why.unpaired) parts.push('Nenhuma conta que você conectou mostra a saída correspondente, então o dinheiro vem de fora.');
  if (why.parkedInTransfers) parts.push('Hoje esses lançamentos estão parados em Transferências, fora do seu orçamento.');
  if (why.categoryDrift.length > 0) {
    parts.push(`O banco classificou de jeitos diferentes ao longo do tempo (${why.categoryDrift.join(', ')}).`);
  }
  if (why.descriptorSuggestsBillPayment) {
    parts.push('A descrição parece pagamento de fatura, mas não bate com nenhum cartão conectado.');
  }
  if (why.descriptorSuggestsInvestment) {
    parts.push('A descrição parece uma aplicação, mas o banco não classificou como investimento.');
  }
  return parts.length > 0
    ? parts.join(' ')
    : 'Esse grupo se repete e ainda não tem uma classificação sua.';
}

// ---------------------------------------------------------------------------
// Selection → derived value. The API's monthlyEstimate covers the whole stream;
// a partial selection re-derives it the same way: sum of the selected rows over
// the distinct calendar months they cover.
// ---------------------------------------------------------------------------

export interface SelectionStats {
  selected: StreamTransaction[];
  total: number;
  monthly: number;
}

export function selectionStats(
  transactions: StreamTransaction[],
  excludedIds: string[],
): SelectionStats {
  const selected = transactions.filter((tx) => !excludedIds.includes(tx.id));
  const months = new Set(selected.map((tx) => tx.date.slice(0, 7)));
  const sum = selected.reduce((acc, tx) => acc + tx.amount, 0);
  return {
    selected,
    total: transactions.length,
    monthly: selected.length > 0 ? sum / months.size : 0,
  };
}

export function selectionHint(stats: SelectionStats): string {
  if (stats.selected.length === 0) return 'Selecione ao menos um lançamento para responder.';
  if (stats.selected.length < stats.total) {
    return `A resposta vale só para os ${stats.selected.length} selecionados. Os outros ficam para a revisão no app.`;
  }
  return 'A resposta vale para todos os lançamentos deste grupo.';
}

export function resultNote(stats: SelectionStats, deferred: boolean): string {
  if (deferred) return 'Vamos perguntar de novo no app. Nada muda agora.';
  if (stats.selected.length < stats.total) {
    return `Vale para ${stats.selected.length} dos ${stats.total} lançamentos. Os outros ficam para a revisão no app.`;
  }
  return `Vale para os ${stats.total} lançamentos deste grupo.`;
}

// ---------------------------------------------------------------------------
// Step 2 state — one reducer over the whole streams surface so the coupling
// rules (empty selection clears the answer; answering closes the follow-up)
// live in one pure place.
// ---------------------------------------------------------------------------

export type StreamAnswer =
  | { kind: 'deferred' }
  | { kind: 'classified'; class: SemanticClassWire; semanticUpdatedCount: number; deferredToReview: number };

export interface StreamsState {
  /** Snapshot of the candidate list — confirmations drop streams from the
   * server's recomputed list, but answered cards stay on screen. Later loads
   * may APPEND new groups (a second bank's post-import recompute); they never
   * remove or replace what is already showing. */
  streams: StreamCandidate[];
  answers: Record<string, StreamAnswer>;
  excluded: Record<string, string[]>;
  txOpen: Record<string, boolean>;
  whyOpen: Record<string, boolean>;
  /** streamKey of the card waiting on the "É renda" perimeter follow-up. */
  incomeFollowUpFor: string | null;
  /** streamKeys with a confirmation POST in flight — a set, so one card settling
   * never re-enables another card's still-pending buttons. */
  posting: string[];
  /** Totals-check classes the user marked "Está certo". */
  checkedTotals: SemanticClassWire[];
  /** Class whose "Falta algo" panel is open. */
  missingFor: SemanticClassWire | null;
}

export const initialStreamsState: StreamsState = {
  streams: [],
  answers: {},
  excluded: {},
  txOpen: {},
  whyOpen: {},
  incomeFollowUpFor: null,
  posting: [],
  checkedTotals: [],
  missingFor: null,
};

export type StreamsAction =
  | { type: 'streams_loaded'; streams: StreamCandidate[] }
  | { type: 'toggle_tx_list'; streamKey: string }
  | { type: 'toggle_why'; streamKey: string }
  | { type: 'toggle_transaction'; streamKey: string; transactionId: string }
  | { type: 'select_all'; streamKey: string }
  | { type: 'clear_selection'; streamKey: string }
  | { type: 'open_income_follow_up'; streamKey: string }
  | { type: 'cancel_income_follow_up' }
  | { type: 'posting'; streamKey: string }
  | { type: 'post_failed'; streamKey: string }
  | { type: 'answered'; streamKey: string; answer: StreamAnswer }
  | { type: 'check_total'; class: SemanticClassWire }
  | { type: 'open_missing'; class: SemanticClassWire }
  | { type: 'close_missing' }
  /** Account ids of a connection the user DELETED — its streams leave the
   * snapshot. Removed ids, never a "live set": useItems can legitimately serve
   * items with incomplete accounts, and pruning from an incomplete live set
   * would drop groups for connections that still exist. */
  | { type: 'prune_accounts'; accountIds: string[] };

export function streamsReducer(state: StreamsState, action: StreamsAction): StreamsState {
  switch (action.type) {
    case 'streams_loaded': {
      // An empty snapshot takes the fresh list whole; a populated one only
      // APPENDS keys it does not hold yet. Never remove or replace: the
      // server's recompute drops answered streams, and mirroring that would
      // drop the cards the user just answered — while ignoring later loads
      // outright starved the snapshot of a second bank's post-import groups
      // (Continuar unlocked onto a Categorias that omitted them).
      if (state.streams.length === 0) {
        return action.streams.length === 0 ? state : { ...state, streams: action.streams };
      }
      const known = new Set(state.streams.map((s) => s.streamKey));
      const fresh = action.streams.filter((s) => !known.has(s.streamKey));
      if (fresh.length === 0) return state;
      // Fresh groups invalidate the totals verification, mirroring the
      // deletion path: the amounts the user marked "Está certo" were computed
      // WITHOUT the new bank's rows, and an open review panel may list them
      // stale.
      return {
        ...state,
        streams: [...state.streams, ...fresh],
        checkedTotals: [],
        missingFor: null,
      };
    }
    case 'toggle_tx_list':
      return {
        ...state,
        txOpen: { ...state.txOpen, [action.streamKey]: !state.txOpen[action.streamKey] },
      };
    case 'toggle_why':
      return {
        ...state,
        whyOpen: { ...state.whyOpen, [action.streamKey]: !state.whyOpen[action.streamKey] },
      };
    // Selection edits on an ANSWERED card are refused: the result note describes the
    // ids the confirmation actually posted, and no second confirmation is sent.
    case 'toggle_transaction': {
      if (state.answers[action.streamKey]) return state;
      const current = state.excluded[action.streamKey] ?? [];
      const next = current.includes(action.transactionId)
        ? current.filter((id) => id !== action.transactionId)
        : [...current, action.transactionId];
      return { ...state, excluded: { ...state.excluded, [action.streamKey]: next } };
    }
    case 'select_all':
      if (state.answers[action.streamKey]) return state;
      return { ...state, excluded: { ...state.excluded, [action.streamKey]: [] } };
    case 'clear_selection': {
      if (state.answers[action.streamKey]) return state;
      const stream = state.streams.find((s) => s.streamKey === action.streamKey);
      if (!stream) return state;
      return {
        ...state,
        excluded: {
          ...state.excluded,
          [action.streamKey]: stream.transactions.map((tx) => tx.id),
        },
      };
    }
    case 'open_income_follow_up':
      return { ...state, incomeFollowUpFor: action.streamKey };
    case 'cancel_income_follow_up':
      return { ...state, incomeFollowUpFor: null };
    case 'posting':
      return state.posting.includes(action.streamKey)
        ? state
        : { ...state, posting: [...state.posting, action.streamKey] };
    case 'post_failed':
      return { ...state, posting: state.posting.filter((key) => key !== action.streamKey) };
    case 'answered':
      // A slow confirmation can land after its connection was deleted and the
      // stream pruned — recording it would resurrect an orphan answer and
      // inflate the summary counts. Settle the in-flight flag, keep nothing.
      if (!state.streams.some((s) => s.streamKey === action.streamKey)) {
        return { ...state, posting: state.posting.filter((key) => key !== action.streamKey) };
      }
      return {
        ...state,
        answers: { ...state.answers, [action.streamKey]: action.answer },
        incomeFollowUpFor:
          state.incomeFollowUpFor === action.streamKey ? null : state.incomeFollowUpFor,
        posting: state.posting.filter((key) => key !== action.streamKey),
      };
    case 'check_total':
      return state.checkedTotals.includes(action.class)
        ? state
        : {
            ...state,
            checkedTotals: [...state.checkedTotals, action.class],
            missingFor: state.missingFor === action.class ? null : state.missingFor,
          };
    case 'open_missing':
      return { ...state, missingFor: action.class };
    case 'close_missing':
      return { ...state, missingFor: null };
    // The snapshot deliberately survives server recomputes so ANSWERED cards stay
    // on screen — but a deleted connection is a different exit: its transactions
    // are gone, and keeping its groups would ask questions about data that no
    // longer exists. Deletion prunes; answering never does.
    case 'prune_accounts': {
      const gone = new Set(action.accountIds);
      const removed = new Set(
        state.streams.filter((s) => gone.has(s.accountId)).map((s) => s.streamKey),
      );
      // ANY deletion invalidates the totals verification, streams or not: the
      // amounts the user marked "Está certo" were computed with the deleted
      // account's rows in them, and an open review panel may show stale rows.
      if (removed.size === 0) {
        return state.checkedTotals.length === 0 && state.missingFor === null
          ? state
          : { ...state, checkedTotals: [], missingFor: null };
      }
      const drop = <T,>(record: Record<string, T>): Record<string, T> => {
        const next = { ...record };
        for (const key of removed) delete next[key];
        return next;
      };
      return {
        ...state,
        streams: state.streams.filter((s) => !gone.has(s.accountId)),
        answers: drop(state.answers),
        excluded: drop(state.excluded),
        txOpen: drop(state.txOpen),
        whyOpen: drop(state.whyOpen),
        posting: state.posting.filter((key) => !removed.has(key)),
        checkedTotals: [],
        missingFor: null,
        incomeFollowUpFor:
          state.incomeFollowUpFor !== null && removed.has(state.incomeFollowUpFor)
            ? null
            : state.incomeFollowUpFor,
      };
    }
    default:
      return state;
  }
}

/** Cards addressed in any way — classified OR deferred. Drives "is the step done". */
export function answeredCount(state: StreamsState): number {
  return Object.keys(state.answers).length;
}

/** Cards that actually WROTE a classification — a deferred card wrote nothing and
 * must never be summarized as classified. */
export function classifiedCount(state: StreamsState): number {
  return Object.values(state.answers).filter((a) => a.kind === 'classified').length;
}

export function deferredCount(state: StreamsState): number {
  return Object.values(state.answers).filter((a) => a.kind === 'deferred').length;
}

/** Unanswered streams whose options can still confirm `cls` — the "Falta algo"
 * candidates a class can claim in one click. */
export function missingCandidatesFor(
  state: StreamsState,
  cls: SemanticClassWire,
): StreamCandidate[] {
  return state.streams.filter(
    (s) =>
      !state.answers[s.streamKey] &&
      streamCopy(s.suggestedClass).options.some((o) => o.class === cls),
  );
}

// ---------------------------------------------------------------------------
// Counted rows — the list behind one class total. The headline is a MONTHLY
// figure while the rows span every month the class reached, so a flat list can
// never be reconciled with it. Grouping by month, each group carrying its own
// subtotal, is what makes the headline checkable instead of merely asserted.
// ---------------------------------------------------------------------------

export interface CountedMonth {
  /** 'YYYY-MM' — also the sort key. */
  key: string;
  label: string;
  /** Raw sum of the group's rows, so subtotal and rows always agree on sign. */
  subtotal: number;
  transactions: StreamTransaction[];
}

const MONTH_LABEL = new Intl.DateTimeFormat('pt-BR', {
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC',
});

/** Newest month first, mirroring the order the API serves the rows in. */
export function groupByMonth(transactions: StreamTransaction[]): CountedMonth[] {
  const groups = new Map<string, CountedMonth>();
  for (const tx of transactions) {
    const key = tx.date.slice(0, 7);
    let group = groups.get(key);
    if (!group) {
      group = {
        key,
        label: MONTH_LABEL.format(new Date(`${key}-01T00:00:00Z`)),
        subtotal: 0,
        transactions: [],
      };
      groups.set(key, group);
    }
    group.subtotal += tx.amount;
    group.transactions.push(tx);
  }
  return [...groups.values()].sort((a, b) => b.key.localeCompare(a.key));
}

/**
 * What the list does and does not cover. The API caps the rows at the newest N
 * while `rowCount` keeps the true total, so staying silent would ask the user to
 * audit rows they cannot see — and the oldest month shown is then partial.
 */
export function countedSummary(shown: number, rowCount: number, monthCount: number): string {
  if (shown < rowCount) {
    return `Mostrando os ${shown} lançamentos mais recentes de ${rowCount}. Os meses mais antigos podem aparecer incompletos.`;
  }
  const rows = `${rowCount} ${rowCount === 1 ? 'lançamento' : 'lançamentos'}`;
  const months = `${monthCount} ${monthCount === 1 ? 'mês' : 'meses'}`;
  return `${rows} em ${months}.`;
}

// ---------------------------------------------------------------------------
// Small formatters
// ---------------------------------------------------------------------------

/** DS money rule: positive FLOWS carry an explicit "+" (balances stay unsigned). */
export function withExplicitPlus(text: string, value: number): string {
  return value > 0 ? `+${text}` : text;
}

/** ISO date → "dd/mm" — the compact form the stream rows use. */
export function shortDate(iso: string): string {
  const [, month, day] = iso.slice(0, 10).split('-');
  return `${day}/${month}`;
}
